import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { Paragraph, Anchor, List, DataSelect, Pagination } from "@zillow/constellation";
import { dollar } from "../../node_modules/@zillow/formatters/dist/formatters.esm.esm.js";
import { last } from "lodash";
import PropTypes from "prop-types";
import { useState, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import Table from "../PaymentDonutChart/Table.esm.js";
import { handleFieldClickEvent, handleFieldChangeEvent } from "./AmortizationCalculator.Clickstream.esm.js";
import { useLastValidAmortizationValues, getZillowURL } from "./AmortizationCalculator.utils.esm.js";
import { FORM_FIELD } from "./constants.esm.js";
const AMORTIZATION_BREAKDOWN_ID = "amortization-breakdown";
const TABLE_DISPLAY_MODE = {
  MONTH: "month",
  YEAR: "year"
};
const OPTIONS = {
  "Show by month": TABLE_DISPLAY_MODE.MONTH,
  "Show by year": TABLE_DISPLAY_MODE.YEAR
};
const withDollar = (key) => (data) => /* @__PURE__ */ jsx(Fragment, { children: dollar(data[key]) });
const COMMON_COLUMNS = [{
  Header: "Interest",
  accessor: "monthlyInterest",
  Cell: withDollar("monthlyInterest")
}, {
  Header: "Principal",
  accessor: "monthlyPrincipal",
  Cell: withDollar("monthlyPrincipal")
}, {
  Header: "Principal remaining",
  accessor: "remainingBalance",
  Cell: withDollar("remainingBalance")
}];
const DATE_BASED_COLUMNS = [{
  Header: "Date",
  accessor: "formattedDate"
}, ...COMMON_COLUMNS];
const MONTH_BASED_COLUMNS = [{
  Header: "Month",
  accessor: "month"
}, ...COMMON_COLUMNS];
const MODES = {
  MONTH_BASED: "month",
  DATE_BASED: "date"
};
const getPageSizeForShowByValue = (showBy) => showBy === TABLE_DISPLAY_MODE.YEAR ? 10 : 12;
const AmortizationScheduleTable = ({
  mode
}) => {
  const form = useFormContext();
  const amortization = useLastValidAmortizationValues(form).calculations.amortization;
  const [showBy, setShowBy] = useState(TABLE_DISPLAY_MODE.MONTH);
  const [pageSize, setPageSize] = useState(getPageSizeForShowByValue(showBy));
  const [currentPage, setCurrentPage] = useState(0);
  useEffect(() => {
    setCurrentPage(0);
    setPageSize(getPageSizeForShowByValue(showBy));
  }, [showBy]);
  const filteredAmortization = useMemo(() => showBy === TABLE_DISPLAY_MODE.YEAR ? amortization.filter((x) => x.month % 12 - 1 === 0) : amortization, [showBy, amortization]);
  const handleDataSelectChange = (e) => {
    if (e.target.value === "year") {
      handleFieldChangeEvent("Show by year", FORM_FIELD);
    } else if (e.target.value === "month") {
      handleFieldChangeEvent("Show by month", FORM_FIELD);
    }
    setShowBy(e.target.value);
  };
  const amortizationPage = useMemo(() => filteredAmortization.slice(currentPage * pageSize, (currentPage + 1) * pageSize), [filteredAmortization, pageSize, currentPage]);
  const {
    totalPrincipal,
    totalInterest
  } = last(amortization) || {};
  const breakdownContent = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(Paragraph, { marginTop: "xs", children: [
      "Our mortgage amortization schedule makes it easy to see how much of your mortgage payment will go toward paying interest and principal over your loan term. You can view amortization by month or year. Keep in mind, your monthly mortgage payment may also include",
      " ",
      /* @__PURE__ */ jsxs(Anchor, { href: getZillowURL("mortgage-learning/what-is-piti/"), children: [
        "property taxes and home insurance",
        " "
      ] }),
      " ",
      "- which aren't included in this amortization schedule, since the payments may fluctuate throughout your loan term."
    ] }),
    /* @__PURE__ */ jsxs(List, { "aria-live": "polite", "aria-atomic": "true", marginY: "xs", children: [
      /* @__PURE__ */ jsxs(List.Item, { children: [
        "Total principal payments: ",
        dollar(totalPrincipal)
      ] }),
      /* @__PURE__ */ jsxs(List.Item, { children: [
        "Total interest payments: ",
        dollar(totalInterest)
      ] })
    ] }),
    /* @__PURE__ */ jsx(DataSelect, { marginBottom: "sm", "aria-label": "Amortization schedule breakdown show by time length dropdown", defaultValue: "month", data: OPTIONS, onChange: handleDataSelectChange }),
    /* @__PURE__ */ jsx(Table, { appearance: "zebra", columns: mode === MODES.DATE_BASED ? DATE_BASED_COLUMNS : MONTH_BASED_COLUMNS, rowKeyAccessor: "remainingBalance", items: amortizationPage, "aria-label": "Amortization schedule breakdown table", "aria-live": "polite", "aria-atomic": "true" }),
    /* @__PURE__ */ jsx(Pagination, { marginTop: "sm", "aria-label": `Amortization breakdown pagination navigation by ${showBy}`, currentPageIndex: currentPage, onPageSelected: setCurrentPage, totalPages: Math.ceil(filteredAmortization.length / pageSize), onClick: () => handleFieldClickEvent("Prev/Next", "button") })
  ] });
  return /* @__PURE__ */ jsx("div", { id: AMORTIZATION_BREAKDOWN_ID, children: breakdownContent });
};
AmortizationScheduleTable.propTypes = {
  amortization: PropTypes.arrayOf(PropTypes.shape({
    month: PropTypes.number,
    monthlyInterest: PropTypes.number,
    monthlyPrincipal: PropTypes.number,
    remainingBalance: PropTypes.number,
    formattedDate: PropTypes.string
  })),
  mode: PropTypes.oneOf(Object.values(MODES)),
  useCardStyle: PropTypes.bool
};
AmortizationScheduleTable.defaultProps = {
  mode: MODES.DATE_BASED,
  useCardStyle: false
};
export {
  AMORTIZATION_BREAKDOWN_ID,
  MODES,
  AmortizationScheduleTable as default
};
